import { useLocation, useNavigate } from 'react-router';
import { routes } from '@/constants/Constants';
import { usePermission } from '@/hooks/usePermission';
import { UserPermissions } from '@/constants/permissions.constant';
import useRenterInviteDrawerStore from '@/stores/useRenterInviteDrawerStore';
import {
  buildInviteNavigationState,
  resolveInviteEntryPoint,
  type OpenInviteSource,
} from '../utils';

/**
 * Opens the renter-invite drawer, ensuring the drawer-open state is its own history entry so
 * Back peels off the drawer instead of leaving the user on a different page.
 *
 * - On App Tracker, with App Tracker access → openDrawer in place; the sync hook pushes the
 *   ?renter-invite=true entry.
 * - Off App Tracker, with App Tracker access → navigate to App Tracker (no query param), then
 *   openDrawer; the sync hook pushes the ?renter-invite=true entry on top.
 * - Without App Tracker access → openDrawer on the current page.
 */
export const useRenterInviteAction = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { openDrawer } = useRenterInviteDrawerStore();

  const { notSet: appTrackerNotSet, enabled: appTrackerEnabled } = usePermission(
    UserPermissions.Deal.CanAccessDealsDashboard,
  );
  const canAccessAppTracker = appTrackerNotSet || appTrackerEnabled;

  const isOnAppTracker = location.pathname === routes.dealsDashboard;

  const openInvite = (source?: OpenInviteSource) => {
    const inviteEntryPoint = resolveInviteEntryPoint(isOnAppTracker, source);
    const navigationState = buildInviteNavigationState(inviteEntryPoint, source);

    if (canAccessAppTracker && !isOnAppTracker) {
      navigate(routes.dealsDashboard, { state: navigationState });
    }

    openDrawer(inviteEntryPoint, navigationState.fromUrl);
  };

  return { openInvite };
};
